// Copyright 2020 Drivetrain Hub LLC
// Vuex module for managing state of organization library data.
// `namespaced` option applies to getters, actions, and mutations; state is always namespaced.

import {pop} from "../../assets/js/utils";
import {apolloMutate, apolloFieldsString} from "../../config/apollo";
import requests from "../../requests";
import gql from "graphql-tag";

export default {
  namespaced: false,
  state: {},
  getters: {},
  mutations: {},
  actions: {
    // Most API queries are defined in the library dialogs, since it is the only place they are used.
    // Most API mutations are defined in the modeler tabs, since it is the only place they are used.

    // PLACEHOLDER {improve} implement optimisticResponse in each update mutation
    // PLACEHOLDER {improve} enable updating of embedded document fields
    //  ==> Then refactor mutation methods in ModelGearMacro, ModelBasicRack, ModelHob.

    // ABSTRACT
    async updateLibObject({ dispatch }, payload) {
      // API mutation mapper to update library object fields.
      // payload should include only fields to update, plus fields: Id, __typename.

      // identify library object type
      const typename = pop(payload, '__typename');

      switch (typename) {
        case requests.gear.TYPENAME.GEAR_DETAIL:
          return await dispatch('updateGearDetail', payload);
        case requests.tool.TYPENAME.BASIC_RACK:
          return await dispatch('updateBasicRack', payload);
        case requests.tool.TYPENAME.HOB:
          return await dispatch('updateHob', payload);
        case requests.tool.TYPENAME.PINION_CUTTER:
          return await dispatch('updatePinionCutter', payload);
      }
    },

    // FRAMES
    async createFrame({ commit }, payload) {
      // API mutation to create new library frame.
      // payload must include all fields required by FrameInput type.
      // Unlike gears, there is not a collection of frames to update in cache.
      // Since frame is one-to-one with part, notification is not called here.
      // Returns created frame.

      commit('SET_STATUS', {message: 'Creating frame'});

      const mutate = {
        variables: {
          data: {...payload}
        },
        mutation: gql`mutation createFrame ($data: FrameInput!) {
            createFrame (data: $data) {
              frame {
                Id
                name
                number
                description
                ${requests.gear.Frame}
              }
            }
          }`,
      };
      const {data: {createFrame: {frame}}} = await apolloMutate(mutate)
        .finally(() => commit('CLEAR_STATUS'));

      return frame;
    },
    async updateFrame({ commit }, payload) {
      // API mutation to update library frame, e.g. shaft or carrier.
      // payload should include only fields to update, plus Id field.
      // Apollo cache automatically updates.
      // Returns nothing.

      commit('SET_STATUS', {message: 'Updating frame'});

      const mutate = {
        variables: {
          data: {...payload}
        },
        mutation: gql`mutation updateFrame ($data: UpdateFrameInput!) {
            updateFrame (data: $data) {
              frame {
                ${requests.lib.Frame}
              }
            }
          }`,
      };
      await apolloMutate(mutate).finally(() => commit('CLEAR_STATUS'));
    },
    async deleteFrame({ commit }, Id) {
      // API mutation to delete library frame.
      // Unlike gears, there is not a collection of frames to update in cache.
      // Returns boolean to indicate if delete was successful.

      commit('SET_STATUS', {message: 'Deleting frame'});

      const mutate = {
        variables: { Id: Id },
        mutation: gql`mutation deleteFrame ($Id: ID!) {
            deleteFrame (Id: $Id) {
              success
            }
          }`,
      };
      const {data: {deleteFrame: {success}}} = await apolloMutate(mutate)
        .finally(() => commit('CLEAR_STATUS'));

      return success;
    },

    // GEARS
    async updateGearDetail({ commit }, payload) {
      // API mutation to update detailed gear.
      // payload should include only fields to update, plus Id field.
      // Updating embedded document fields is not supported, due to query string.
      // Apollo cache automatically updates.

      commit('SET_STATUS', {message: 'Updating gear'});

      const mutate = {
        variables: {
          data: {...payload}
        },
        mutation: gql`mutation updateGearDetail ($data: UpdateGearDetailInput!) {
            updateGearDetail (data: $data) {
              gearDetail {
                ${apolloFieldsString(Object.keys(payload))}
              }
            }
          }`,
      };
      await apolloMutate(mutate).finally(() => commit('CLEAR_STATUS'));
    },

    // TOOLS
    async updateBasicRack({ commit }, payload) {
      // API mutation to update basic rack.
      // payload should include only fields to update, plus Id field.
      // Updating embedded document fields is not supported, due to query string.
      // Apollo cache automatically updates.

      commit('SET_STATUS', {message: 'Updating basic rack'});

      const mutate = {
        variables: {
          data: {...payload}
        },
        mutation: gql`mutation updateBasicRack ($data: UpdateBasicRackInput!) {
            updateBasicRack (data: $data) {
              basicRack {
                ${apolloFieldsString(Object.keys(payload))}
              }
            }
          }`,
      };
      await apolloMutate(mutate).finally(() => commit('CLEAR_STATUS'));
    },
    async updateHob({ commit }, payload) {
      // API mutation to update hob.
      // payload should include only fields to update, plus Id field.
      // Updating embedded document fields is not supported, due to query string.
      // Apollo cache automatically updates.

      commit('SET_STATUS', {message: 'Updating hob'});

      const mutate = {
        variables: {
          data: {...payload}
        },
        mutation: gql`mutation updateHob ($data: UpdateHobInput!) {
            updateHob (data: $data) {
              hob {
                ${apolloFieldsString(Object.keys(payload))}
              }
            }
          }`,
      };
      await apolloMutate(mutate).finally(() => commit('CLEAR_STATUS'));
    },
    async updatePinionCutter({ commit }, payload) {
      // API mutation to update pinion cutter.
      // payload should include only fields to update, plus Id field.
      // Updating embedded document fields is not supported, due to query string.
      // Apollo cache automatically updates.

      commit('SET_STATUS', {message: 'Updating pinion cutter'});

      const mutate = {
        variables: {
          data: {...payload}
        },
        mutation: gql`mutation updatePinionCutter ($data: UpdatePinionCutterInput!) {
            updatePinionCutter (data: $data) {
              pinionCutter {
                ${apolloFieldsString(Object.keys(payload))}
              }
            }
          }`,
      };
      await apolloMutate(mutate).finally(() => commit('CLEAR_STATUS'));
    },
  },
};
